import { format } from 'date-fns-tz';

import { Etapa, ItemDaLegenda } from 'pages/MinhaEquipe/calendario-do-ciclo';

import { FASE_SEM_CADASTRO_ECOA, FaseEcoa } from './acoes-do-semestre';

/**
 * Etapas das Ações ECOA para o `CalendarioDoCiclo` (ECN-27) e para a régua da fila (ECN-44), das mesmas fontes de
 * `getFutureCalendarEcoa`: Cadastramento e Ciente e Validação de cada fase mensal; Apreciação Bonificação da fase
 * semestral. A fase `68af4da8…` (sem cadastramento ECOA) fica de fora.
 */

/** Legenda própria das três etapas ECOA, nos tipos do calendário compartilhado. */
export const LEGENDA_DO_CALENDARIO_ECOA: ItemDaLegenda[] = [
  { tipo: 'cad', nome: 'Cadastramento', cor: 'bg-brand-blue' },
  { tipo: 'val', nome: 'Ciente e Validação', cor: 'bg-brand-green' },
  { tipo: 'n3', nome: 'Apreciação Bonificação', cor: 'bg-brand-pink-light' },
];

const ORDEM = LEGENDA_DO_CALENDARIO_ECOA.map(item => item.tipo);

/** "Nível 3 - 2º Semestre" → "2º Semestre", como o `cleanTitle` do calendário antigo. */
function semPrefixoDeNivel(titulo: string): string {
  return titulo.replace(/^Nível\s*\d+\s*-\s*/, '');
}

function etapasDaFase(fase: FaseEcoa): Etapa[] {
  const referencia = semPrefixoDeNivel(fase.title ?? '');

  if (fase.type === 'month') {
    return [
      { tipo: 'cad', inicio: fase.date_start, fim: fase.date_finish, referencia },
      {
        tipo: 'val',
        inicio: fase.evaluation_ecoa_days_step2_start as number,
        fim: fase.evaluation_ecoa_days_step2_finish as number,
        referencia,
      },
    ];
  }

  if (fase.type === 'semester') {
    return [
      { tipo: 'n3', inicio: fase.date_evaluation_step5_start as number, fim: fase.date_evaluation_step5_finish as number, referencia },
    ];
  }

  return [];
}

/** ECN-27: etapas ECOA do ciclo, sem repetir, em ordem de início. */
export function etapasEcoaDoCiclo(fases: FaseEcoa[] | undefined): Etapa[] {
  const unicas = new Map<string, Etapa>();

  (fases ?? [])
    .filter(fase => fase._id !== FASE_SEM_CADASTRO_ECOA)
    .flatMap(etapasDaFase)
    .filter(etapa => typeof etapa.inicio === 'number' && typeof etapa.fim === 'number')
    .forEach(etapa => unicas.set(`${etapa.tipo}:${etapa.inicio}:${etapa.fim}`, etapa));

  return Array.from(unicas.values()).sort((a, b) => a.inicio - b.inicio || ORDEM.indexOf(a.tipo) - ORDEM.indexOf(b.tipo));
}

export interface BlocoDaReguaEcoa {
  titulo: 'Cadastramento' | 'Ciente e Validação' | 'Bonificação';
  /** "dd/MM a dd/MM"; sem datas, `undefined`. */
  periodo: string | undefined;
  /** O período contém o horário do servidor (`aria-current="step"`). */
  atual: boolean;
}

const CAMPOS_DO_BLOCO: [BlocoDaReguaEcoa['titulo'], keyof FaseEcoa, keyof FaseEcoa][] = [
  ['Cadastramento', 'date_start', 'date_finish'],
  ['Ciente e Validação', 'evaluation_ecoa_days_step2_start', 'evaluation_ecoa_days_step2_finish'],
  ['Bonificação', 'date_evaluation_step5_start', 'date_evaluation_step5_finish'],
];

/** ECN-44: blocos da régua "Fluxo de apreciação da ação ECOA" para a fase em exibição na fila. */
export function reguaDaFaseEcoa(fase: FaseEcoa | undefined, agora: number): BlocoDaReguaEcoa[] {
  return CAMPOS_DO_BLOCO.map(([titulo, campoDeInicio, campoDeFim]) => {
    const inicio = fase?.[campoDeInicio] as number | undefined;
    const fim = fase?.[campoDeFim] as number | undefined;
    const temDatas = typeof inicio === 'number' && typeof fim === 'number';

    return {
      titulo,
      periodo: temDatas ? `${format(new Date(inicio), 'dd/MM')} a ${format(new Date(fim), 'dd/MM')}` : undefined,
      atual: temDatas && inicio <= agora && agora <= fim,
    };
  });
}

/**
 * "Andamento da ação" do cartão da fila (ECN-54). Na opção semestral a página exibe uma fase sintética cujo
 * cadastramento cobre o ciclo inteiro; ali a etapa do cartão é a Bonificação, e só ela fica atual (edge case "fase
 * semestral" da spec). A régua do topo da fila (ECN-44) não passa por aqui: usa `reguaDaFaseEcoa`, a regra literal.
 */
export function reguaDaFilaEcoa(fase: FaseEcoa | undefined, agora: number, semestral: boolean): BlocoDaReguaEcoa[] {
  const blocos = reguaDaFaseEcoa(fase, agora);
  return semestral ? blocos.map(bloco => ({ ...bloco, atual: bloco.titulo === 'Bonificação' })) : blocos;
}
